import React from 'react';
import {
    Button, Card, Grid, IconButton, Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Translate } from 'react-localize-redux';
import FieldGroupFieldInput from './FieldGroupFieldInput';
import { IFieldGroupSchema } from '../../constants/types';
import { defaultEmptyFieldValue, parseStoredFieldValue } from '../../utils/fieldValueCoercion';
import {
    EditInstance, EditSubform, activeSubforms, emptyInstance, fieldAnchorId, isClosedGroup, isEditable,
} from '../../utils/fieldGroupEdit';

/**
 * Controlled: renders `instances` and reports every change through `onChange`. The
 * edit state (including the `minRepeats` seeding) lives in SectionForm, built by
 * hydrateFieldGroupEdit.
 */
interface FieldGroupFormProps {
    fieldGroup: IFieldGroupSchema;
    instances: EditInstance[];
    onChange: (instances: EditInstance[]) => void;
    country?: string;
    uuidPatient?: string;
    uuidInvestigation?: string;
    uuidSurvey?: string;
    /** subform state keyed by trigger option id (multioption_with_subforms), see fieldGroupEdit */
    subforms?: Record<string, EditSubform>;
    onSubformChange?: (optionId: number, subform: EditSubform) => void;
    /** options for a `forwardedFrom` field, which come from another field's current value */
    resolveForwardedOptions?: (field: any) => { value: string; label: string }[] | undefined;
    /** options for an `instance-selector` field, which are another group's live instances (SKIN-689) */
    resolveInstanceOptions?: (field: any) => { value: string; label: string }[] | undefined;
}

const FieldGroupForm: React.FC<FieldGroupFormProps> = ({
    fieldGroup,
    instances,
    onChange,
    country,
    uuidPatient,
    uuidInvestigation,
    uuidSurvey,
    subforms,
    onSubformChange,
    resolveForwardedOptions,
    resolveInstanceOptions,
}) => {
    function addInstance() {
        const next = instances.length === 0 ? 0 : Math.max(...instances.map(i => i.instanceNumber)) + 1;
        onChange([...instances, emptyInstance(fieldGroup, next)]);
    }

    function removeInstance(instanceNumber: number) {
        onChange(instances.filter(i => i.instanceNumber !== instanceNumber));
    }

    function updateFieldValue(instanceNumber: number, fieldId: number, value: unknown) {
        onChange(instances.map(inst => (inst.instanceNumber !== instanceNumber ? inst : {
            ...inst,
            surveyRecords: inst.surveyRecords.map(rec => (rec.surveyField.id === fieldId ? { ...rec, value } : rec)),
        })));
    }

    function renderInput(field: any, value: unknown, name: string, onValue: (next: unknown) => void) {
        return (
            <FieldGroupFieldInput
                key={name}
                name={name}
                field={field}
                value={value}
                onChange={onValue}
                country={country}
                uuidPatient={uuidPatient}
                uuidInvestigation={uuidInvestigation}
                uuidSurvey={uuidSurvey}
            />
        );
    }

    function renderSubforms(field: any, instance: EditInstance, value: unknown) {
        if (!onSubformChange || !subforms) return null;
        return activeSubforms(field, value, subforms).map(({ option, subform, instance: subInstance }) => {
            const subformGroup = option.subformGroup;
            const subFields = [...(subformGroup.fields || [])].sort((a: any, b: any) => a.order - b.order);
            return (
                <Card key={`subform-${instance.instanceNumber}-${option.id}`} variant="outlined" sx={{ p: 1.5, mb: 1, ml: 2, borderStyle: 'dashed' }}>
                    <Typography variant="caption" color="textSecondary">{subformGroup.name}</Typography>
                    {subFields.filter((f: any) => isEditable(f, subInstance)).map((subField: any) => {
                        const record = subInstance.surveyRecords.find(r => r.surveyField.id === subField.id);
                        return (
                            <div key={subField.id} id={fieldAnchorId(fieldGroup.id, instance.instanceNumber, subField.id, option.id)}>
                                {renderInput(subField, record?.value, `sf_${option.id}_field_${subField.id}`, (next) => {
                                    const nextInstance = {
                                        ...subInstance,
                                        surveyRecords: subInstance.surveyRecords.map(r => (r.surveyField.id === subField.id ? { ...r, value: next } : r)),
                                    };
                                    onSubformChange(option.id, { ...subform, instances: [nextInstance, ...subform.instances.slice(1)] });
                                })}
                            </div>
                        );
                    })}
                </Card>
            );
        });
    }

    function renderField(schemaField: any, instance: EditInstance) {
        const resolvedOptions = schemaField.forwardedFrom
            ? resolveForwardedOptions?.(schemaField)
            : schemaField.type === 'instance-selector'
                ? resolveInstanceOptions?.(schemaField)
                : undefined;
        const field = resolvedOptions ? { ...schemaField, options: resolvedOptions } : schemaField;
        // Encrypted fields (e.g. `sex`, mirrored from the patient's personal data) are not
        // editable here; they stay in the instance untouched, so they are never sent.
        if (!isEditable(field, instance)) return null;
        const record = instance.surveyRecords.find(r => r.surveyField.id === field.id);
        const value = parseStoredFieldValue(
            record?.value ?? defaultEmptyFieldValue(field.type),
            field.type,
        );
        return (
            <div key={field.id} id={fieldAnchorId(fieldGroup.id, instance.instanceNumber, field.id)} style={{ marginBottom: '0.5rem' }}>
                {renderInput(field, value, `fg_${fieldGroup.id}_${instance.instanceNumber}_field_${field.id}`,
                    (nextValue) => updateFieldValue(instance.instanceNumber, field.id, nextValue))}
                {renderSubforms(field, instance, value)}
            </div>
        );
    }

    // SKIN-444: a closed group with nothing recorded has nothing to edit and nothing to add.
    if (isClosedGroup(fieldGroup) && instances.length === 0) return null;

    const sortedFields = [...fieldGroup.fields].sort((a, b) => a.order - b.order);
    const maxAllowed = fieldGroup.repeats ? fieldGroup.maxRepeats : (fieldGroup.minRepeats > 0 ? fieldGroup.minRepeats : 1);
    const canAdd = (fieldGroup.repeats || instances.length < fieldGroup.minRepeats) && (maxAllowed == null || instances.length < maxAllowed);
    const canRemove = instances.length > fieldGroup.minRepeats;

    return (
        <div style={{ marginBottom: '1rem' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                {fieldGroup.name}
            </Typography>
            {fieldGroup.description && (
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    {fieldGroup.description}
                </Typography>
            )}
            {[...instances]
                .sort((a, b) => a.instanceNumber - b.instanceNumber)
                .map((instance, idx) => (
                    <Card key={instance.instanceNumber} variant="outlined" sx={{ p: 2, mb: 1, position: 'relative' }}>
                        <Grid container alignItems="center" sx={{ mb: 1 }}>
                            <Grid item xs>
                                <Typography variant="caption" color="textSecondary">
                                    {fieldGroup.name} #{idx + 1}
                                </Typography>
                            </Grid>
                            {canRemove && (
                                <Grid item>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => removeInstance(instance.instanceNumber)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Grid>
                            )}
                        </Grid>
                        {sortedFields.map(field => renderField(field, instance))}
                    </Card>
                ))}
            {canAdd && (
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addInstance}
                    sx={{ mt: 1 }}
                >
                    <Translate id="general.add" /> {fieldGroup.name}
                </Button>
            )}
        </div>
    );
};

export default FieldGroupForm;
